import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { getToken, PLAN_INACTIVE_CODE, PLAN_INACTIVE_MESSAGE, removeToken } from "../shared/helpers/localstorage-helper";
import { useEffect, useState } from "react";
import { getMe } from "../features/search/service/user-service";
import { getRoleById } from "../features/search/service/role-service";
import { User } from "../shared/types/user";
import { ProjectLoader } from "../components/project-loader/project-loader";

interface ProtectedRouteProps {
  allowedRoles: string[];
}

type RouteRole = {
  name?: string | null;
  description?: string | null;
};

const normalizeRole = (role?: string | null) => role?.toLowerCase().trim() || "";

const getRoleNames = async (user: User): Promise<string[]> => {
  let role: RouteRole | null | undefined = user.role;

  if (!role && user.role_id) {
    role = await getRoleById(user.role_id);
  }

  return [
    normalizeRole(role?.name),
    normalizeRole(role?.description),
  ].filter(Boolean);
};

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ allowedRoles }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const token = getToken();
  const allowedRoleKey = allowedRoles.map(normalizeRole).filter(Boolean).sort().join("|");
  const requiresRole = Boolean(allowedRoleKey);
  const [isChecking, setIsChecking] = useState(Boolean(token));
  const [isAuthorized, setIsAuthorized] = useState(!requiresRole && Boolean(token));

  useEffect(() => {
    if (token === null) {
        removeToken();
        navigate("/", { replace: true });
    }
  }, [token, navigate]);

  useEffect(() => {
    if (token === null) {
      return;
    }

    let isMounted = true;

    const validateSession = async () => {
      setIsChecking(true);

      try {
        const user = await getMe();

        if (!requiresRole) {
          if (isMounted) {
            setIsAuthorized(true);
          }
          return;
        }

        const roleNames = await getRoleNames(user);
        const normalizedAllowedRoles = allowedRoleKey.split("|").filter(Boolean);
        const hasAllowedRole = roleNames.some((roleName) => normalizedAllowedRoles.includes(roleName));

        if (!hasAllowedRole) {
          navigate("/home", { replace: true });
        }

        if (isMounted) {
          setIsAuthorized(hasAllowedRole);
        }
      } catch (error) {
        const sessionError = error as Error & { code?: string; status?: number };

        if (isMounted && (sessionError.code === PLAN_INACTIVE_CODE || sessionError.status === 403)) {
          removeToken();
          navigate("/", {
            replace: true,
            state: { error: PLAN_INACTIVE_MESSAGE }
          });
          setIsAuthorized(false);
        } else if (isMounted && !requiresRole) {
          setIsAuthorized(true);
        } else if (isMounted) {
          setIsAuthorized(false);
        }
      } finally {
        if (isMounted) {
          setIsChecking(false);
        }
      }
    };

    validateSession();

    return () => {
      isMounted = false;
    };
  }, [allowedRoleKey, location.pathname, navigate, requiresRole, token]);
  
  if (token === null) {
    return null;
  }

  if (isChecking) {
    return (
      <div className="project-loader-screen">
        <ProjectLoader label="Carregando sessão" />
      </div>
    );
  }

  if (!isAuthorized) {
    return null;
  }

  return <Outlet />;
};

export default ProtectedRoute;
